/**
 * Cross stitch borders — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_stitch_charts`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const crossStitchBordersPack: ToilePack = {
    slug: 'cross-stitch-borders',
    category: 'craft',
    name: 'Cross stitch borders',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1216, h: 832},
    readings: [
        {key: 'floral', label: 'Floral', subject: 'a cross-stitch chart of a floral border band, small roses and leaves repeating along the length, the repeat bracketed and a corner turn shown at the right end'},
        {key: 'geometric', label: 'Geometric', subject: 'a cross-stitch chart of three narrow geometric borders stacked on one grid, a zigzag, a row of diamonds and a Greek key, each in two colours'},
        {key: 'folk', label: 'Folk', subject: 'a cross-stitch chart of a folk border in red and black, stylised birds and eight-point stars alternating along the band'},
    ],
    styles: [
        {key: 'colour', label: 'Colour chart', style: 'a clean colour chart, each cross stitch a small flat coloured X filling its square, fine light-gray grid lines, a heavier line every ten squares', palette: 'a limited palette of the floss colours named', framing: 'the chart centred on a plain white sheet with an even margin'},
        {key: 'symbol', label: 'Symbol chart', style: 'a symbol chart for black-and-white printing, each stitch one small black symbol on white, one symbol per floss colour, a heavier line every ten squares', palette: 'black ink only', framing: 'the chart filling most of a white sheet with a narrow clean margin'},
        {key: 'graph', label: 'Graph paper', style: 'a hand-coloured worksheet, stitches filled in with felt-tip pens, slightly uneven strokes, on blue squared graph paper', palette: 'bright felt-tip colours on pale blue grid paper', framing: 'the chart drawn on a plain sheet with a small margin all round'},
    ],
    directions: ['christmas', 'ocean', 'lace edge'],
    directionHint: 'try: christmas',
    seo: {
        title: 'Cross stitch borders: floral, geometric, folk',
        metaDescription: 'Nine cross stitch border charts: floral, geometric and folk repeats, in colour, symbol and hand-coloured styles.',
        h1: 'Cross stitch borders: floral, geometric and folk repeats',
        tagline: 'Coloured border repeats on a counting grid, ready to run along a towel edge or a sampler.',
        intro: 'A border frames a sampler, finishes a towel edge or runs along a bookmark. These nine charts give three repeats: a floral band with a corner turn, three stacked geometric borders, and a red and black folk band. Each comes as a colour chart, a symbol chart for black-and-white printing, or a hand-coloured worksheet. Type a different border and the same repeat layout carries it.',
        useCases: [
            'a sampler frame',
            'a towel edge',
            'a bookmark strip',
            'a card border',
        ],
        faq: [
            {q: 'How do I work out the repeat?', a: 'Count one repeat across the chart, then stitch it as many times as your length needs.'},
            {q: 'What is a corner turn?', a: 'The way a border changes direction at a corner. The floral chart shows one at the right end.'},
            {q: 'Which style prints easily?', a: 'The symbol chart, for black-and-white printing.'},
        ],
    },
}
